import { useEffect, useState } from "react";
import { api, adminAuth, imageUrl } from "../../lib/api";
import { toast } from "sonner";
import { Link2, Trash2, Plus } from "lucide-react";

export default function PresetGroupsTab({ products = [], onChanged }) {
  const [groups, setGroups] = useState([]);
  const [newName, setNewName] = useState("");
  const [newMembers, setNewMembers] = useState([]); // product ids
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/preset-groups", { headers: adminAuth() });
      setGroups(r.data);
    } catch { toast.error("Couldn't load preset sets"); }
  };
  useEffect(() => { load(); }, []);

  const toggleNewMember = (id) =>
    setNewMembers((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const create = async () => {
    if (newMembers.length < 1) { toast.error("Pick at least one product to share presets"); return; }
    setCreating(true);
    try {
      await api.post("/admin/preset-groups", { name: newName.trim(), product_ids: newMembers }, { headers: adminAuth() });
      setNewName(""); setNewMembers([]);
      toast.success("Shared preset set created");
      await load();
      await onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't create set"); }
    finally { setCreating(false); }
  };

  const toggleMember = async (group, productId) => {
    const memberIds = group.members.map((m) => m.id);
    const next = memberIds.includes(productId) ? memberIds.filter((x) => x !== productId) : [...memberIds, productId];
    try {
      const r = await api.patch(`/admin/preset-groups/${group.id}`, { product_ids: next }, { headers: adminAuth() });
      setGroups((prev) => prev.map((g) => g.id === group.id ? r.data : g));
      await onChanged?.();
    } catch { toast.error("Update failed"); }
  };

  const rename = async (group, name) => {
    if (name.trim() === group.name) return;
    try {
      const r = await api.patch(`/admin/preset-groups/${group.id}`, { name: name.trim() }, { headers: adminAuth() });
      setGroups((prev) => prev.map((g) => g.id === group.id ? r.data : g));
      toast.success("Renamed");
    } catch { toast.error("Rename failed"); }
  };

  const remove = async (group) => {
    if (!window.confirm(`Unlink "${group.name}"? Products keep their current designs but stop sharing new ones.`)) return;
    try {
      await api.delete(`/admin/preset-groups/${group.id}`, { headers: adminAuth() });
      toast.success("Set removed");
      await load();
      await onChanged?.();
    } catch { toast.error("Couldn't remove set"); }
  };

  return (
    <div className="card-rustic" data-testid="admin-preset-groups">
      <div className="p-5 border-b border-[rgba(27,42,65,0.1)]">
        <div className="font-mono-ui text-[10px] tracking-[0.3em] text-[var(--crimson)]">/PRESET SETS</div>
        <div className="font-serif-display text-2xl text-[var(--navy)]">Shared Preset Sets</div>
        <div className="text-xs text-[var(--ink-soft)] mt-1">Group products so they share the same preset designs. Add or remove a design on any product in a set and it updates on all of them automatically.</div>
      </div>

      {/* CREATE */}
      <div className="p-5 border-b border-[rgba(27,42,65,0.06)]" data-testid="preset-group-create">
        <div className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-[var(--ink-soft)] mb-3">Create a new set</div>
        <input
          className="input-field max-w-md"
          placeholder="Set name (e.g. Family Designs)"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          data-testid="preset-group-name-input"
        />
        <div className="mt-3 text-[11px] text-[var(--ink-soft)]">Pick the products that should share presets (existing designs will be merged):</div>
        <div className="mt-2 flex flex-wrap gap-2">
          {products.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => toggleNewMember(p.id)}
              data-testid={`preset-group-new-member-${p.id}`}
              className={`px-3 py-1.5 rounded-full border text-xs font-mono-ui transition ${newMembers.includes(p.id) ? "border-[var(--gold)] bg-[var(--navy)] text-[var(--bone)]" : "border-[rgba(27,42,65,0.2)] text-[var(--navy)] hover:border-[var(--navy)]"}`}
            >
              {p.name} <span className="opacity-60">· {p.category}</span>
            </button>
          ))}
          {products.length === 0 && <div className="text-sm text-[var(--ink-soft)]">No products yet.</div>}
        </div>
        <button onClick={create} disabled={creating} className="btn-primary mt-4 text-sm" data-testid="preset-group-create-button">
          <Plus className="w-3.5 h-3.5"/> {creating ? "Creating…" : "Create Set"}
        </button>
      </div>

      {/* EXISTING */}
      <div className="divide-y divide-[rgba(27,42,65,0.06)]">
        {groups.map((g) => {
          const memberIds = g.members.map((m) => m.id);
          return (
            <div key={g.id} className="p-5" data-testid={`preset-group-${g.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-2 flex-1">
                  <Link2 className="w-4 h-4 text-[var(--crimson)]"/>
                  <input
                    defaultValue={g.name}
                    onBlur={(e) => rename(g, e.target.value)}
                    className="font-serif-display text-xl text-[var(--navy)] bg-transparent border-b border-transparent hover:border-[rgba(27,42,65,0.2)] focus:border-[var(--navy)] outline-none"
                    data-testid={`preset-group-name-${g.id}`}
                  />
                </div>
                <button
                  onClick={() => remove(g)}
                  data-testid={`preset-group-delete-${g.id}`}
                  className="inline-flex items-center gap-1.5 text-[var(--crimson)] hover:bg-[var(--crimson)]/10 px-2 py-1.5 rounded-sm font-mono-ui text-[10px] tracking-[0.2em] uppercase transition"
                >
                  <Trash2 className="w-3.5 h-3.5"/> Unlink
                </button>
              </div>

              <div className="mt-3 font-mono-ui text-[10px] tracking-[0.25em] uppercase text-[var(--ink-soft)]">Shared designs ({g.presets.length})</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {g.presets.map((d) => (
                  <div key={d.id} className="w-12 h-12 rounded-sm border border-[rgba(27,42,65,0.15)] bg-[var(--bone-2)] overflow-hidden" data-testid={`preset-group-thumb-${d.id}`}>
                    <img src={imageUrl(d.image_url)} alt={d.name} className="w-full h-full object-contain p-1"/>
                  </div>
                ))}
                {g.presets.length === 0 && <div className="text-xs text-[var(--ink-soft)]">No designs yet — add one from any product in this set.</div>}
              </div>

              <div className="mt-4 font-mono-ui text-[10px] tracking-[0.25em] uppercase text-[var(--ink-soft)]">Products in this set ({memberIds.length})</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {products.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => toggleMember(g, p.id)}
                    data-testid={`preset-group-member-${g.id}-${p.id}`}
                    className={`px-3 py-1.5 rounded-full border text-xs font-mono-ui transition ${memberIds.includes(p.id) ? "border-[var(--gold)] bg-[var(--navy)] text-[var(--bone)]" : "border-[rgba(27,42,65,0.2)] text-[var(--navy)] hover:border-[var(--navy)]"}`}
                  >
                    {p.name} <span className="opacity-60">· {p.category}</span>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
        {groups.length === 0 && <div className="p-8 text-center text-[var(--ink-soft)] text-sm">No shared sets yet. Create one above.</div>}
      </div>
    </div>
  );
}
